import styled from "@emotion/styled";
import React, { useState, useEffect } from "react";
import { useTheme } from "@emotion/react";
import { FAQStudentsAccordionSection } from "./FAQStudentsAccordionSection";
import { FAQTeachersAccordionSection } from "./FAQTeachersAccordionSection";
import { MotivationSection } from "./MotivationSection";
import { InfoPagesLayout } from "../../../components/layout/info-pages-layout";
import { FormattedMessage } from "react-intl";
import { 
  School as SchoolIcon,
  Person as PersonIcon,
  Help as HelpIcon,
  ContactMail as ContactIcon,
  Menu as MenuIcon,
  QuestionAnswer as QuestionIcon,
  Support as SupportIcon
} from "@mui/icons-material";

export const HelpPage = () => {
  const themeContext = useTheme();
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = [
    { id: 'motivation', title: 'Einführung', icon: <SchoolIcon /> },
    { id: 'students-faq', title: 'FAQs Studenten', icon: <PersonIcon /> },
    { id: 'teachers-faq', title: 'FAQs Lehrer', icon: <SupportIcon /> },
    { id: 'contact', title: 'Kontakt', icon: <ContactIcon /> }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = window.innerWidth <= 768 ? 120 : 220;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <InfoPagesLayout title={<FormattedMessage id={"footer.faq"} />}>
        <PageContainer>
          <TableOfContents>
            <TocTitle>
              <MenuIcon />
              Inhaltsverzeichnis
            </TocTitle>
            <TocList>
              {sections.map((section) => (
                <TocItem 
                  key={section.id}
                  active={activeSection === section.id}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.icon}
                  {section.title}
                </TocItem>
              ))}
            </TocList>
          </TableOfContents>

          <ContentWrapper>
            <Section id="motivation">
              <SectionHeader>
                <SchoolIcon />
                <h2>Einfach und Effektiv mit Blagoom</h2>
              </SectionHeader>
              <MotivationSection />
            </Section>

            <Section id="students-faq">
              <SectionHeader>
                <PersonIcon />
                <h2>Häufige Fragen - Studenten</h2>
              </SectionHeader>
              <FAQStudentsAccordionSection />
            </Section>

            <Section id="teachers-faq">
              <SectionHeader>
                <SupportIcon />
                <h2>Häufige Fragen - Lehrer</h2>
              </SectionHeader>
              <FAQTeachersAccordionSection />
            </Section>

            <Section id="contact">
              <SectionHeader>
                <ContactIcon />
                <h2>Kontakt & Support</h2>
              </SectionHeader>
              <ContactSection>
                <ContactCard>
                  <ContactText>
                    <FormattedMessage id="help-page.contact.text" />
                  </ContactText>
                  <EmailLink>
                    <FormattedMessage id="help-page.contact.email" />
                  </EmailLink>
                </ContactCard>
              </ContactSection>
            </Section>
          </ContentWrapper>
        </PageContainer>
      </InfoPagesLayout>
    </>
  );
};


// Styled Components
const PageContainer = styled.div`
  display: flex;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  margin-top: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    margin: 0;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    gap: 0.5rem;
  }
`;

const TableOfContents = styled.div`
  position: sticky;
  top: 220px;
  width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  height: fit-content;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  flex-shrink: 0;

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    width: 100%;
    max-height: none;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 8px;
  }
`;

const TocTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  color: ${props => props.theme.mainTextColor};
  font-size: 1.1rem;
  font-weight: 600;
`;

const TocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  color: ${props => props.active ? props.theme.primaryColor : props.theme.mainTextColor};
  background: ${props => props.active ? props.theme.primary + '20' : 'transparent'};
  border-left: 3px solid ${props => props.active ? props.theme.primaryColor : 'transparent'};

  &:hover {
    background: ${props => props.theme.primary + '10'};
    transform: translateX(4px);
  }

  svg {
    font-size: 1.1rem;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: hidden;
  }
`;

const Section = styled.section`
  margin-bottom: 3rem;
  scroll-margin-top: 220px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    scroll-margin-top: 120px;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.primaryColor};

  h2 {
    margin: 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 2rem;
    font-weight: 600;
  }

  svg {
    font-size: 2rem;
    color: ${props => props.theme.primaryColor};
  }

  @media (max-width: 768px) {
    h2 {
      font-size: 1.5rem;
    }
    
    svg {
      font-size: 1.5rem;
    }
  }
`;

const ContactSection = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const ContactCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 2px solid ${props => props.theme.primaryColor};

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ContactText = styled.div`
  margin-bottom: 1rem;
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.mainTextColor};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const EmailLink = styled.div`
  font-weight: 600;
  font-size: 1.2rem;
  color: ${props => props.theme.primaryColor};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    text-decoration: underline;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;
